import React from 'react';
import LazyImage from 'shared/assets/LazyImage';
import { AssetType } from 'shared/assets/types';
import ProductPricing from 'shared/components/commerce/ProductPricing';
import { toKebabCase } from 'util/StringUtil';
import { GetPriceForQuantity, GetPricingLine } from '../pricing/pricing-result';
import { productAssetPlaceholder } from '../product-details/ProductAssets';
import { ProductAsset } from '../product-details/types';
import { RecentlyViewedProduct as Model } from './useRecentlyViewedProducts';
import { dispatchRocEvent } from 'shared/hooks/useRocEventHandler';

interface RecentlyViewedProductProps {
	product: Model;
	useTinySliderLazyLoading?: boolean;
}

/**
 * Renders a single product that was recently viewed
 */
export default function RecentlyViewedProduct(props: RecentlyViewedProductProps) {
	const { product } = props;
	const compareAssets = (a: ProductAsset, b: ProductAsset) => a.sortOrder - b.sortOrder;
	const defaultAsset = product?.assets?.sort(compareAssets).find((asset) => asset.assetType === AssetType.Image);
	const mainImage = defaultAsset ?? productAssetPlaceholder;

	let customUrl: string = '#';
	if (product?.summary?.meta?.url) {
		customUrl = product.summary.meta.url;
	}

	const line = product.displayPricing && GetPricingLine(product.displayPricing, product.id);
	const displayPrice = line && GetPriceForQuantity(line.prices, 1);

	return (
		<div className="roc-product-list__item">
			<a
				href={customUrl}
				className="roc-product-list__link"
				onClick={() =>
					dispatchRocEvent('product-clicked', {
						productId: product.id,
						url: customUrl,
						productName: product.summary ? product.summary.name : '',
						productGroupId: product.summary?.productGroupId ?? null,
					})
				}
			>
				{mainImage !== null ? (
					<div className="roc-product-list__image-wrapper">
						{props.useTinySliderLazyLoading ? (
							// Use tiny-slider's built-in lazy image loading to lazily load the image
							<img
								src={
									mainImage.mainImageUrl
										? mainImage.mainImageUrl
										: `/images/placeholder/placeholder-image.png`
								}
								data-src={mainImage.mainImageUrl}
								alt={mainImage.altText}
								className="roc-product-list__image tns-lazy-img"
							/>
						) : (
							<LazyImage
								src={mainImage.mainImageUrl}
								alt={mainImage.altText}
								className="roc-product-list__image"
							/>
						)}
					</div>
				) : null}
				{product.summary ? (
					<h3
						className="roc-product-list__title"
						data-testid={`rvp-header-${toKebabCase(product.summary.name)}`}
					>
						{product.summary.name}
					</h3>
				) : null}

				<ProductPricing displayPrice={displayPrice} className="roc-product-list" />
			</a>
		</div>
	);
}
